import React, { useState } from "react";
import { Badge, Loading } from "../../../ForBrands/ui/BrandUI";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import TeamArchetypeModal from "./TeamArchetypeModal";
import { factorValueLabel, factorsRanked, radarGeometry, teamArchetype } from "./teamModel";

// Figma radar: 542 x 378, rings at 25 / 50 / 75 / 100 (35px apart).
const VIEW = { width: 542, height: 378 };
const CENTER = { cx: 271, cy: 189, radius: 140 };
const RINGS = [0.25, 0.5, 0.75, 1];
const GRID = "#E8E8EC";
const BRAND = "#FF4400";

// Phone radar (2409:1225): a 306 x 268 plot, rings 20px apart (80px outer),
// 10px points. Each label is a 34px block (the 13/18 code over the 11/16
// "raw · pct") set 8px beyond the end of its spoke: centred above EFF and
// below BALL, and beside the four side spokes, vertically centred on them.
// The baselines sit where the 18px and 16px line boxes put them.
const PHONE = {
  view: { width: 306, height: 268 },
  center: { cx: 153, cy: 134, radius: 80 },
  dot: 5,
  gap: 8,
  block: 34,
  codeBaseline: 13.7,
  valueBaseline: 30,
};

const phoneLabel = (axis) => {
  const { cx, cy } = PHONE.center;
  if (Math.abs(axis.x - cx) < 1) {
    const top = axis.y < cy ? axis.y - PHONE.gap - PHONE.block : axis.y + PHONE.gap;
    return { x: axis.x, top, anchor: "middle" };
  }
  const right = axis.x > cx;
  return { x: right ? axis.x + PHONE.gap : axis.x - PHONE.gap, top: axis.y - PHONE.block / 2, anchor: right ? "start" : "end" };
};

// Each factor plotted at its league percentile, labelled "raw · percentile".
const TeamStyleRadar = ({ factors, phone = false }) => {
  const view = phone ? PHONE.view : VIEW;
  const center = phone ? PHONE.center : CENTER;
  const axes = radarGeometry(factors, center);
  const summary = axes.map((a) => `${a.code} ${factorValueLabel(a.factor)}`).join(", ");
  return (
    <svg
      viewBox={`0 0 ${view.width} ${view.height}`}
      role="img"
      aria-label={`Team style, raw value and league percentile: ${summary}`}
      className={phone ? "h-auto w-full max-w-[306px] self-center" : "h-auto w-full max-w-[542px] self-center"}
    >
      {RINGS.map((r) => (
        <circle key={r} cx={center.cx} cy={center.cy} r={center.radius * r} fill="none" stroke={GRID} />
      ))}
      {axes.map((a) => (
        <line key={a.code} x1={center.cx} y1={center.cy} x2={a.axis.x} y2={a.axis.y} stroke={GRID} />
      ))}
      <polygon
        points={axes.map((a) => `${a.vertex.x},${a.vertex.y}`).join(" ")}
        fill={BRAND}
        fillOpacity={0.24}
        stroke={BRAND}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {axes.map((a) => (
        <circle key={`dot-${a.code}`} cx={a.vertex.x} cy={a.vertex.y} r={phone ? PHONE.dot : 6} fill={BRAND} stroke="#fff" strokeWidth={2} />
      ))}
      {axes.map((a) => {
        if (phone) {
          const label = phoneLabel(a.axis);
          return (
            <text key={`label-${a.code}`} x={label.x} textAnchor={label.anchor}>
              <tspan x={label.x} y={label.top + PHONE.codeBaseline} fill="#2c2c2c" fontSize={13} fontWeight={600}>
                {a.code}
              </tspan>
              <tspan x={label.x} y={label.top + PHONE.valueBaseline} fill="#757575" fontSize={11}>
                {factorValueLabel(a.factor)}
              </tspan>
            </text>
          );
        }
        return (
          <text key={`label-${a.code}`} x={a.label.x} y={a.label.y} textAnchor="middle">
            <tspan x={a.label.x} dy="-0.2em" fill="#2c2c2c" fontSize={13} fontWeight={600}>
              {a.code}
            </tspan>
            <tspan x={a.label.x} dy="1.4em" fill="#757575" fontSize={11}>
              {factorValueLabel(a.factor)}
            </tspan>
          </text>
        );
      })}
    </svg>
  );
};

// Who the radar's percentiles rank against (B5: the peer group beside the
// ranks): the team style's league cohort, which is not the game analytics'
// peer group the snapshot names. Null without a cohort size.
export const cohortLede = (style) => {
  const n = Number(style?.cohortTeams);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `Percentile vs the ${n} teams with box scores in this league${style.season ? `, ${style.season}` : ""}.`;
};

// Why there is no radar yet.
const emptyNote = (style) => {
  if (style?.reason === "feed_error") return "The league feed is unavailable right now. Team style returns once it is back.";
  if (style?.available && !factorsRanked(style.factors)) return "Team style needs at least 8 teams with box scores in your league this season.";
  return "Team style appears once your league feed has this season's box scores.";
};

// Team Archetype (Figma 02b; BUILD_SPEC B7, B8: the app's own card and
// rules, beside the snapshot): the archetype badge, what the archetype
// means (in words under the badge, as the athlete's Player Archetype card
// puts its tag's description; a hover-only title reached neither the
// keyboard nor a phone) and the six-factor radar, in a collapsible card
// (open by default, B9), its (i) button opening what each factor
// measures. The button sits beside the title, never folding the card; the
// modal lives outside the card body, so it opens over a folded card too.
// Phones (2409:1215): the phone card with a bm-h-s title, Figma's own 20px
// info icon (2409:1220) and the phone radar; the info button opens the
// sheet (2410:1047). `collapse` is the page's {id, open, onToggle}
// (Collapse all). `showCohort` (the game analytics are on screen, their
// snapshot naming another peer group) adds the radar's own group as the
// lede; without the analytics the snapshot's league cells name this very
// cohort beside it.
const TeamArchetypeCard = ({ style, loading, collapse, showCohort = false }) => {
  const phone = usePhoneTheme();
  const [open, setOpen] = useState(false);
  const factors = style?.available ? style.factors : null;
  const archetype = teamArchetype(factors);
  const lede = showCohort && !loading && archetype ? cohortLede(style) : null;
  return (
    <>
      <CollapsibleCard title="Team Archetype" subtitle={lede} info={{ label: "What the team archetype measures", onOpen: () => setOpen(true) }} {...collapse}>
        {loading && <Loading label="Loading team style..." />}
        {!loading && archetype && (
          <>
            <div className="flex flex-col items-start gap-2">
              <Badge tone="brand">{archetype.label}</Badge>
              {archetype.description && <p className={phone ? "bm-body-s text-bm-muted" : "bm-body-m text-bm-muted"}>{archetype.description}</p>}
            </div>
            <TeamStyleRadar factors={factors} phone={phone} />
          </>
        )}
        {!loading && !archetype && <p className="bm-body-m text-bm-muted">{emptyNote(style)}</p>}
      </CollapsibleCard>
      {open && <TeamArchetypeModal onClose={() => setOpen(false)} />}
    </>
  );
};

export default TeamArchetypeCard;
